import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import ServiceImage from "../components/common/ddr/ServiceImage";
import { ServiceType } from "../components/common/ddr/ddrTypes";

export const CLICK_LOG_KEY = "click_analytics_data";
const GLOBAL_ID_PREFIX = "LSGUID_";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// function to get cookie by name
export function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

export function EpochToDateTime(
  epochSeconds: number,
  format = "24",
  showSeconds = true,
): string {
  let epSec = epochSeconds;
  if (epSec.toString().length < 13) {
    epSec = epSec * 1000;
  }
  const date = new Date(epSec);
  if (isNaN(date.getTime())) {
    return "-";
  }
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  let hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  if (format == "12") {
    const ampm = hours >= "12" ? "PM" : "AM";
    hours = (parseInt(hours) % 12).toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}${showSeconds ? `:${seconds}` : ""} ${ampm}`;
  }
  return `${day}/${month}/${year} ${hours}:${minutes}${showSeconds ? `:${seconds}` : ""}`;
}

const isObject = (variable: any) => {
  return variable !== null && typeof variable === "object";
};

export function AnomalyDescription(json: any) {
  let displayHtml = "";
  if ("data" in json && isObject(json["data"]) && "display" in json["data"]) {
    displayHtml = '<ul style="padding-left: 15px;margin: 0;">';
    for (const k in json["data"]["display"]) {
      const v = json["data"]["display"][k];
      if (k == "Process") {
        displayHtml +=
          "<li>" +
          k +
          ': <a href="#" style="color:#ffffff;" class="viewProcessInfo" data-smac="' +
          json["smac"] +
          '" data-time="' +
          json["epochTime"] +
          '" data-pid="' +
          json["data"]["pid"] +
          '" data-process="' +
          v +
          '">' +
          v +
          "</a></li>";
      } else {
        displayHtml += "<li>" + k + ": " + v + "</li>";
      }
    }
    if ("GlobalID" in json && json["GlobalID"] != "") {
      displayHtml +=
        "<li>Global ID: " + GLOBAL_ID_PREFIX + json["GlobalID"] + "</li>";
    }
    displayHtml += "</ul>";
  }

  return displayHtml;
}

const convertToCSV = (array: any) => {
  const header = Object.keys(array[0]).join(",");
  const rows = array.map((obj: any) => Object.values(obj).join(",")).join("\n");
  return `${header}\n${rows}`;
};

// Function to handle CSV download
export function downloadCSV(data: any, fileName: string) {
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function formatBandwidth(d: number, unit?: string, decimal?: number) {
  d = d || 0;
  if (!unit) {
    unit = "B";
  }

  if (!decimal) {
    decimal = 0;
  }

  if (unit == "G") {
    d = d * 1000 * 1000 * 1000;
  } else if (unit == "M") {
    d = d * 1000 * 1000;
  } else if (unit == "K") {
    d = d * 1000;
  }

  if (d >= 1000 * 1000 * 1000 * 1000) {
    return (d / (1000 * 1000 * 1000 * 1000)).toFixed(decimal) + " TB";
  } else if (d >= 1000 * 1000 * 1000) {
    return (d / (1000 * 1000 * 1000)).toFixed(decimal) + " GB";
  } else if (d >= 1000 * 1000) {
    return (d / (1000 * 1000)).toFixed(decimal) + " MB";
  } else if (d >= 1000) {
    return (d / 1000).toFixed(decimal) + " KB";
  } else {
    return d.toFixed(decimal) + " " + unit;
  }
}

export function getTextType(value: string, log?: string) {
  const IpRegex =
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/g;
  const PortRegex = /^([0-9]{1,5})$/g;
  const userNameRegex = /^([a-zA-Z\d]{3,20})$/g;
  const macRegex = /^([0-9a-fA-F][0-9a-fA-F]:){5}([0-9a-fA-F][0-9a-fA-F])$/g;
  // const hashRegex = /^([a-fA-F\d]{32})$/g;
  const hashRegexPatterns = {
    md5: /^[a-fA-F0-9]{32}$/,
    sha1: /^[a-fA-F0-9]{40}$/,
    sha256: /^[a-fA-F0-9]{64}$/,
    sha512: /^[a-fA-F0-9]{128}$/,
  };
  const queryRegex =
    /^(?:[a-zA-Z0-9_.-]+=[^&=\s]+)(?:&[a-zA-Z0-9_.-]+=[^&=\s]+)*$/;
  const domainRegex =
    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{1,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}$/g;
  // const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  let textType;
  if (!value) {
    textType = "string";
  } else if (log === "user_anomalies" && value.match(userNameRegex)) {
    textType = "username";
  } else if (value.match(IpRegex)) {
    textType = "ip"; //also send id.orig_h id.resp_h
  } else if (value.match(PortRegex)) {
    textType = "port"; //also send id.orig_p id.resp_p
  } else if (value.match(macRegex)) {
    textType = "mac";
    // } else if (value.match(hashRegex)) {
  } else if (
    Object.values(hashRegexPatterns).some((pattern) => pattern.test(value))
  ) {
    textType = "hash"; //md5, sha1, sha256
  } else if (value.match(domainRegex)) {
    textType = "domain";
  } else if (value.match(userNameRegex)) {
    textType = "username";
  } else if (value.match(emailRegex)) {
    textType = "email";
  } else if (value.match(queryRegex)) {
    textType = "query";
  } else if (value === "*") {
    textType = "*";
  } else {
    textType = "string";
  }
  return textType;
}

export function capitalizeWords(str: string) {
  const words = str.split(" ");
  const capitalizedWords = words.map((word: string) => {
    return word[0].toUpperCase() + word.slice(1);
  });
  return capitalizedWords.join(" ");
}

export function sortJson(data: any) {
  const sortedData: any = {};
  Object.keys(data)
    .sort()
    .forEach((key) => {
      sortedData[key] = data[key];
    });
  return sortedData;
}

export function getShadow360URL(data: {
  anomalyId?: number;
  startTime: number;
  endTime: number;
  logTypes: Array<string>;
  searchValue: any;
  searchType?: any;
  sensor?: string;
}) {
  const tempLinkflowConnectionList: Array<string> = [];
  // if (data.logTypes.includes("linkflow")) {
  //   tempLinkflowConnectionList.push("_conn_log");
  // }
  if (data.logTypes.includes("_conn_log")) {
    tempLinkflowConnectionList.push("linkflow");
  }
  data.logTypes.push(...tempLinkflowConnectionList);

  if (data.searchType == "user" && data.logTypes.includes("user_anomalies")) {
    if (data.searchValue) {
      const searchValueSplitted: Array<string> = data.searchValue.split("@");
      data.searchValue = searchValueSplitted[0] + "*";
    }
  }

  const queryAndPlainTextData = JSON.stringify({
    textFieldValue: data.searchValue,
    textLogType: data.logTypes,
    textType: data.searchType,
    aId: data.anomalyId,
  });

  const s360Url = `/shadow360/?date={"startDate":"${data.startTime}","endDate":"${data.endTime}"}&plainText=${queryAndPlainTextData}&smartSearch=0&query=${queryAndPlainTextData}&sensorDetails=${data.sensor || "master"}`;

  return encodeURI(s360Url);
}

// TO get query builder search for single field for a single log
export function getShadow360QueryBuilderURL(data: {
  anomalyId?: number;
  startTime: number;
  endTime: number;
  logSrcs: Array<string>;
  fieldValObj: Array<{ fieldName: string; fieldValue: any; logSrc?: string }>;
  condition?: "AND" | "OR";
  searchType?: any;
  sensor?: string;
  operation?: string;
}) {
  const tempLinkflowConnectionList: Array<string> = [];
  if (data.logSrcs.includes("_conn_log")) {
    tempLinkflowConnectionList.push("linkflow");
  }
  data.logSrcs.push(...tempLinkflowConnectionList);

  const condition = data.condition ?? "OR";

  const ruleTemplate = {
    id: 1,
    rule_id: 100,
    log_source: "__LOG_SOURCE_PLACEHOLDER__",
    field_name: "__FIELD_NAME_PLACEHOLDER__",
    values: data.operation,
    external_conditions_set: [
      {
        method: data.operation,
        args: { value: "__VALUE_PLACEHOLDER__", _method: "" },
        result: true,
      },
    ],
    external_operation_set: [],
  };

  const rules: any = [];
  // Map from logSrc to the list of rule ids belonging to it
  const logSrcRuleIds: Record<string, number[]> = {};
  let ruleCounter = 1;

  data.logSrcs.forEach((logSrc) => {
    logSrcRuleIds[logSrc] = [];
    data.fieldValObj.forEach((fieldVal) => {
      if (fieldVal.logSrc && fieldVal.logSrc !== logSrc) {
        return;
      }
      const rule = JSON.parse(JSON.stringify(ruleTemplate));
      rule.id = ruleCounter;
      rule.rule_id = ruleCounter * 100;
      rule.log_source = logSrc;
      rule.field_name = fieldVal.fieldName;
      rule.external_conditions_set[0].args.value = fieldVal.fieldValue;
      logSrcRuleIds[logSrc].push(ruleCounter);
      rules.push(rule);
      ruleCounter++;
    });
  });

  const groupTemplate = {
    aggregate_rules: [],
    base_rule_joins: [] as string[],
    common_rule_join: [],
    id: 1,
    log_source: "__LOG_SOURCE_PLACEHOLDER__",
  };
  const groups: any = [];
  let groupCounter = 1;

  data.logSrcs.forEach((logSrc) => {
    const groupItem = JSON.parse(JSON.stringify(groupTemplate));
    groupItem.id = groupCounter;
    groupItem.log_source = logSrc;
    // Interleave rule ids with the condition operator: ["1", "AND", "2", ...]
    const joins: string[] = [];
    logSrcRuleIds[logSrc].forEach((id, idx) => {
      joins.push(String(id));
      if (idx < logSrcRuleIds[logSrc].length - 1) {
        joins.push(condition);
      }
    });
    groupItem.base_rule_joins = joins;
    groups.push(groupItem);
    groupCounter++;
  });

  const queryData = JSON.stringify({
    common_rules: [],
    rule_aggregate: [],
    rules: rules,
    group: groups,
    group_join: ["1"],
    log_source: data.logSrcs,
  });

  const s360Url = `/shadow360/?date={"startDate":"${data.startTime}","endDate":"${data.endTime}"}&plainText=&smartSearch=0&query=${queryData}&sensorDetails=${data.sensor || "master"}&isCaseSensitive=0`;

  return encodeURI(s360Url);
}

export function getServiceImage(source: string) {
  source = source.toLowerCase();
  if (source.includes("aws_s3")) {
    return ServiceImage({
      type: ServiceType.AWS,
      variant: "path",
    });
  } else if (source.includes("azure_storage")) {
    return ServiceImage({
      type: ServiceType.AZURE_CLOUD,
      variant: "path",
    });
  } else if (source.includes("azure_onedrive")) {
    return ServiceImage({
      type: ServiceType.AZURE,
      variant: "path",
    });
  } else if (source.includes("dropbox")) {
    return ServiceImage({
      type: ServiceType.DROPBOX,
      variant: "path",
    });
  } else if (
    source.includes("google_storage") ||
    source.includes("google_drive")
  ) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD,
      variant: "path",
    });
  } else if (source.includes("alibaba_oss")) {
    return ServiceImage({
      type: ServiceType.ALIBABA,
      variant: "path",
    });
  } else if (source.includes("salesforce_data_cloud")) {
    return ServiceImage({
      type: ServiceType.SALES_FORCE,
      variant: "path",
    });
  } else if (source.includes("net_suite_store")) {
    return ServiceImage({
      type: ServiceType.NETSUITE,
      variant: "path",
    });
  } else if (source.includes("log_management_ftp")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("log_management_smb")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("log_management_sftp")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("postgresql")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("mssql")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("mysql")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("oracle")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("mongodb")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  } else if (source.includes("teams")) {
    return ServiceImage({
      type: ServiceType.GOOGLE_CLOUD, // No icon for this service
      variant: "path",
    });
  }
}

export function isValidIPv4(ip: string) {
  const ipv4Regex =
    /^(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}$/;
  return ipv4Regex.test(ip);
}

export function isValidSubnetMask(mask: string) {
  const validMasks = [
    "0.0.0.0",
    "128.0.0.0",
    "192.0.0.0",
    "224.0.0.0",
    "240.0.0.0",
    "248.0.0.0",
    "252.0.0.0",
    "254.0.0.0",
    "255.0.0.0",
    "255.128.0.0",
    "255.192.0.0",
    "255.224.0.0",
    "255.240.0.0",
    "255.248.0.0",
    "255.252.0.0",
    "255.254.0.0",
    "255.255.0.0",
    "255.255.128.0",
    "255.255.192.0",
    "255.255.224.0",
    "255.255.240.0",
    "255.255.248.0",
    "255.255.252.0",
    "255.255.254.0",
    "255.255.255.0",
    "255.255.255.128",
    "255.255.255.192",
    "255.255.255.224",
    "255.255.255.240",
    "255.255.255.248",
    "255.255.255.252",
    "255.255.255.254",
    "255.255.255.255",
  ];

  const cidrRegex = /^(?:\d{1,3}\.){3}\d{1,3}\/([0-9]|[12][0-9]|3[0-2])$/;
  return cidrRegex.test(mask) || validMasks.includes(mask);
}

export function getDeviceType() {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/android/.test(userAgent)) {
    return "Android";
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
    return "iOS";
  } else if (/macintosh|mac os x/.test(userAgent)) {
    return "Mac";
  } else if (/windows nt/.test(userAgent)) {
    return "Windows PC";
  } else if (/linux/.test(userAgent)) {
    return "Linux PC";
  } else {
    return "Unknown Device";
  }
}

export function saveClickData(target: HTMLElement) {
  const widget = target.closest(".CUSTOM_LS_WIDGET");

  let title = "";
  let subtitle = "";

  //Header
  //Check if the clicked element has the data-title attribute
  if (target.hasAttribute("data-title")) {
    title = target.getAttribute("data-title")?.trim() || title;
  }

  // console.log(title);

  //Check for closest CLICK_PARENT class and check for first PAGE_TITLE inside of it
  const clickParent = target.closest(".CLICK_PARENT");
  if (clickParent) {
    const pagetitle = clickParent.querySelector(".PAGE_TITLE");
    if (title == "" && pagetitle) {
      title = pagetitle.textContent?.trim() || "";
    }
  }

  //SubHeader
  //Check if the clicked element has the data-subtitle attribute
  if (target.hasAttribute("data-subtitle")) {
    subtitle = target.getAttribute("data-subtitle")?.trim() || "";
  }

  //Check if the clicked element is widget
  if (widget && subtitle == "") {
    const subtitlediv = widget.querySelector(".widgetTitle");
    if (subtitlediv) {
      subtitle = subtitlediv.textContent?.trim() || "";
    }
  }

  //Check if widget has module class which is from old widgets
  const oldWidget = target.closest(".module");
  if (oldWidget) {
    subtitle =
      oldWidget.querySelector(".moduleHeader")?.textContent?.trim() || subtitle;
  }

  const epochTime = new Date().getTime();
  const device = getDeviceType();

  if (!widget) {
    //check if dialog is open
    const dialog = target.closest("[role='dialog']");
    if (dialog) {
      const dialogTitle = dialog.querySelector("h2");
      if (dialogTitle) {
        title = dialogTitle.textContent?.trim() || title;
      }
    }
  }

  if (
    target.tagName.toLowerCase() === "button" &&
    target.textContent &&
    subtitle == ""
  ) {
    subtitle = target.textContent.trim() || subtitle;
  }

  const dataToSave = {
    mainHeaderText: title,
    subHeaderText: subtitle,
    deviceTypeDetails: device,
    timestamp: epochTime,
  };
  const existingData =
    JSON.parse(localStorage.getItem(CLICK_LOG_KEY) || "[]") || [];
  existingData.push(dataToSave);
  localStorage.setItem(CLICK_LOG_KEY, JSON.stringify(existingData));
}

export function formatNumber(num: number) {
  num = num || 0;
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
}

export const sanitizeHtml = (unsafe: string) => {
  try {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  } catch (error) {
    console.log(error, unsafe);
    return unsafe;
  }
};

export const convertToEpochTime = (timeStr: string): number => {
  const date = new Date();

  switch (timeStr.replace(/\s+/g, "").toLowerCase()) {
    case "day":
      date.setUTCDate(date.getUTCDate() - 1);
      break;
    case "3day":
      date.setUTCDate(date.getUTCDate() - 3);
      break;
    case "7day":
      date.setUTCDate(date.getUTCDate() - 7);
      break;
    case "1month":
      date.setUTCMonth(date.getUTCMonth() - 1);
      break;
    case "2month":
      date.setUTCMonth(date.getUTCMonth() - 2);
      break;
  }

  return Math.floor(date.getTime() / 1000);
};

export const convertToHours = (timeStr: string): number => {
  switch (timeStr.replace(/\s+/g, "").toLowerCase()) {
    case "day":
      return 24;
    case "3day":
      return 3 * 24;
    case "7day":
      return 7 * 24;
    case "1month":
      return 30 * 24;
    case "2month":
      return 60 * 24;
    default:
      return 0;
  }
};
